// MIT License - Copyright (c) 2026 Doug McNall
import { useState, useEffect } from 'react';
import './AdminLogin.css';

const apiUrl = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL;

/**
 * Admin password reset confirmation page.
 *
 * Reads the reset token from the ?token= query parameter, accepts a new
 * password (validated server-side regardless), and redirects back to
 * the admin login on success.
 */
const ResetPassword = () => {
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token') || '';
    setToken(t);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setSubmitting(true);
    try {
      const r = await fetch(`${apiUrl}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        setDone(true);
        setTimeout(() => {
          window.location.href = '/admin-tool/';
        }, 3000);
      } else {
        setError(data.detail || 'Reset failed. The link may have expired.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="admin-login">
        <div className="admin-login-container">
          <div className="admin-login-header">
            <h1>D&D Hot Sauce</h1>
            <h2>Invalid Link</h2>
          </div>
          <p style={{ color: 'var(--admin-text-secondary)', lineHeight: 1.6 }}>
            This reset link is invalid or has expired.
          </p>
          <button
            className="login-button"
            style={{ marginTop: '1.5rem' }}
            onClick={() => { window.location.href = '/admin-tool/'; }}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="admin-login">
        <div className="admin-login-container">
          <div className="admin-login-header">
            <h1>D&D Hot Sauce</h1>
            <h2>Password Reset</h2>
          </div>
          <p style={{ color: 'var(--admin-text-secondary)', lineHeight: 1.6 }}>
            Your password has been updated. Redirecting to login...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-login">
      <div className="admin-login-container">
        <div className="admin-login-header">
          <h1>D&D Hot Sauce</h1>
          <h2>Set New Password</h2>
        </div>

        <form onSubmit={handleSubmit} className="admin-login-form">
          <div className="form-group">
            <label htmlFor="newpw">New Password</label>
            <input
              type="password"
              id="newpw"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={submitting}
              minLength={8}
              placeholder="At least 8 characters"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confpw">Confirm Password</label>
            <input
              type="password"
              id="confpw"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              disabled={submitting}
              minLength={8}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="login-button" disabled={submitting}>
            {submitting ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="admin-login-footer">
          <p>Must include uppercase, lowercase, and a number.</p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
